import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  FinanceCreateDimensionDocument,
  FinanceGetCustomDimensionSettingsDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

interface NewDimensionModalProps {
  onClose: (success?: boolean) => void
}

interface CreateNewDimensionFormValues {
  name: string
}

const NewDimensionModal = ({ onClose }: NewDimensionModalProps) => {
  const [submitting, setSubmitting] = React.useState(false)
  const { company } = useCurrentSession()

  const [createDimension] = useMutation(FinanceCreateDimensionDocument, {
    extractorFn: (data) => data.financeMutations.createFinanceCustomDimension,
    refetchQueries: [FinanceGetCustomDimensionSettingsDocument],
  })

  const form = useForm<CreateNewDimensionFormValues>({
    defaultValues: {
      name: '',
    },

    validate: (data) => {
      const errors: FormErrors<CreateNewDimensionFormValues> = {}
      if (!data.name) {
        errors.name = i18n.t('finance.modals.new_dimension.fields.name.errors.mandatory')
      }
      return errors
    },

    onSubmit: async (values) => {
      setSubmitting(true)
      const variables = {
        name: values.name,
        companyId: company.id,
      }

      const result = await createDimension({ variables })

      const handled = handleResult(result, (errors) => {
        form.setErrors({
          name: errors['name'],
        })
      })

      if (handled.success) {
        track(trackingEvents.accounting.dimensionsAddSubmit, {
          status: 'Success',
          assignment_type: 'one value',
        })
        onClose(true)
      }

      setSubmitting(false)
      return handled
    },
  })

  return (
    <Modal
      illustration="document-edit"
      title={i18n.t('finance.modals.new_dimension.title')}
      description={i18n.t('finance.modals.new_dimension.description')}
      onClose={() => onClose()}
      onSubmit={() => form.handleSubmit()}
      footer={
        <Button disabled={submitting || !form.values.name.length} submit>
          {i18n.t('finance.buttons.add_dimension')}
        </Button>
      }
    >
      <Box gap="s24">
        <TextField
          mandatory
          label={i18n.t('finance.modals.new_dimension.fields.name.label')}
          onChange={(value) => form.setValues({ ...form.values, name: value })}
          error={form.errors.name}
        />
      </Box>
    </Modal>
  )
}

export default NewDimensionModal
